import { useEffect, useState } from 'react';

export interface IUseDisclose {
  isOpen: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onClose: () => void;
}

/**
 * Hook used to manage open/closed states
 * ex: modals or collapse
 *
 * @param {boolean} [open=false]
 * @return {*}  {IUseDisclose}
 */
export const useDisclose = (open = false): IUseDisclose => {
  const [isOpen, setIsOpen] = useState(open);
 
  const onToggle = () => setIsOpen((value) => !value);

  const onOpen = () => setIsOpen(true);

  const onClose = () => setIsOpen(false);

  return {
    isOpen,
    onToggle,
    onOpen,
    onClose,
  };
};

interface IUseError {
  error: string;
  showError: (error: string | Error) => void;
  hideError: () => void;
  toggleError: (error?: string | Error) => void;
}

/**
 * Hook used to manage hide/visible error states
 *
 * @param {string} [initialError='']
 * @return {*}  {IUseError}
 */
export const useError = (initialError = ''): IUseError => {
  const [error, setError] = useState(initialError);

  const showError = (newError: string | Error) => {
    if (newError instanceof Error) {
      return setError(newError.message);
    }

    setError(newError || 'Não foi possível realizar a operação desejada');
  };

  const hideError = () => setError('');

  const toggleError = (newError?: string | Error) => {
    if (newError) {
      return showError(newError);
    }

    hideError();
  };

  return {
    error,
    showError,
    hideError,
    toggleError,
  };
};

export interface IUseLoading {
  loading: boolean;
  showLoader: () => void;
  hideLoader: () => void;
  toggleLoader: () => void;
}

/**
 * Hook used to manage hide/visible loading states
 *
 * @param {boolean} [initialState=false]
 * @return {*}  {IUseLoading}
 */
export const useLoader = (initialState = false): IUseLoading => {
  const [loading, setLoading] = useState(initialState);

  const showLoader = () => setLoading(true);

  const hideLoader = () => setLoading(false);

  const toggleLoader = () => setLoading(!loading);

  return {
    loading,
    showLoader,
    hideLoader,
    toggleLoader,
  };
};

/**
 * Custom hook to prevent user from leave site with unsaved changes
 *
 * @param {{
 *   when: boolean;
 *   message: string;
 * }} {
 *   when,
 *   message,
 * }
 */
export const useBeforeUnload = ({
  when,
  message,
}: {
  when: boolean;
  message: string;
}) => {
  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    if (when) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when, message]);
};

